import { CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { CoreModule } from '@core/core.module';
import { MncDmePosRoutingModule as MncDmePosRoutingModule } from './mnc-dmepos-routing.module';
import { MncDmePosComponent as MncDmePosComponent } from './mnc-dmepos.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfigurationService } from '@core/configuration';
import {
  FeatureToggleModule,
  LibDirectiveModule,
  LibPipeModule,
  SecurityModule,
  SecurityService,
} from '@mynexus/mynexus-ui-lib';
import { environment } from '@env/environment';
import {
  AuthorizationServiceComponent
} from '@shared/components/authorization-service/authorization-service.component';
import { ContentTypeInterceptor } from '@core/interceptors';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { API_URL, PLATFORM_API_URL } from '@core/constants';
import { PrimeNGConfigLoader, providePrimeNGFromShell, ShellService, TokenInterceptor } from '@mynexus/mfe-core';
import { DmeposMfeService } from '@core/service/dmepos-mfe.service';
import { AuthenticationService } from './core/service';

function loadConfig(authenticationService: AuthenticationService, securityService: SecurityService) {
  return async () => {
    const permissions = authenticationService.userPermissions;
    securityService.setUserPermissions(permissions.flat());

    // Legacy permissions model force us to set this false to avoid problem with empty permissions users
    securityService.setAllowEmptyPermission(false);
  };
}

/**
 * MFE-specific default theme loader. Lazy-loads `@mynexus/theme` so the Carelon
 * preset is only fetched when the shell hasn't supplied its own theme.
 */
const loadCarelonPrimeNGConfig: PrimeNGConfigLoader = async () => {
  const { Carelon, CSS_LAYERS_ORDER } = await import('@mynexus/theme');

  return {
    ripple: false,
    theme: {
      preset: Carelon,
      options: {
        darkModeSelector: false,
        cssLayer: {
          name: 'primeng',
          order: CSS_LAYERS_ORDER
        }
      },
    }
  };
};

@NgModule({
  declarations: [
    MncDmePosComponent,
  ],
  imports: [
    MncDmePosRoutingModule,
    AuthorizationServiceComponent,
    BrowserModule,
    CoreModule,
    FormsModule,
    FeatureToggleModule,
    FeatureToggleModule.forRoot(`${environment.apiUrl}/api/`),
    LibPipeModule,
    LibDirectiveModule,
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    SecurityModule.forRoot([], environment.apiUrl),
    ...environment.modules,
  ],
  exports: [
    LibPipeModule,
  ],
  providers: [
    providePrimeNGFromShell(loadCarelonPrimeNGConfig),
    ConfigurationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ContentTypeInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    provideAppInitializer(() => {
      const initializerFn = (loadConfig)(inject(AuthenticationService), inject(SecurityService));
      return initializerFn();
    }),
    {
      provide: API_URL,
      useValue: environment.apiUrl,
    },
    {
      provide: PLATFORM_API_URL,
      useValue: environment.platformApiUrl,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MncDmePosModule implements DoBootstrap {
  constructor(
    private injector: Injector,
    shellService: ShellService,
    mfeService: DmeposMfeService
  ) {
    this.registerCustomElement('mnc-dmepos', MncDmePosComponent);
    this.registerCustomElement('mnc-dmepos-authorization-service', AuthorizationServiceComponent);

    shellService.registerMfeService('mnc-dmepos', mfeService);
  }

  ngDoBootstrap() {
    document.dispatchEvent(new CustomEvent('mnc-dmepos-loaded'));
  }

  private registerCustomElement(name: string, component) {
    if (customElements.get(name)) return;
    customElements.define(name, createCustomElement(component, { injector: this.injector }));
  }
}
